import * as THREE from "three";
import {GLTFExporter} from "three/examples/jsm/exporters/GLTFExporter";

const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};

const sanitizeFileName = (name) => {
    let base = String(name || "module-3d").trim();
    if (base.toLowerCase().endsWith(".glb")) {
        base = base.slice(0, -4);
    }
    base = base
        .replace(/[\\/:*?"<>|]+/g, "-")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    return `${base || "module-3d"}.glb`;
};

/** Убрать хелперы/линии выделения из клона перед экспортом. */
const pruneNonMeshHelpers = (root) => {
    const remove = [];
    root.traverse((obj) => {
        if (
            obj.isLine
            || obj.isLineSegments
            || obj.isLineLoop
            || obj.isSprite
            || obj.isAxesHelper
            || obj.isGridHelper
            || obj.isBone
            || obj.type === "TransformControls"
            || obj.type === "TransformControlsGizmo"
            || obj.type === "TransformControlsPlane"
            || obj.userData?.module3dRole === "helper"
        ) {
            remove.push(obj);
        }
    });
    remove.forEach((obj) => {
        obj.parent?.remove(obj);
    });
};

/**
 * Собрать корень только из размещённых деталей сцены Module 3D.
 * @param {THREE.Scene} scene
 * @returns {THREE.Group}
 */
export const buildModuleExportRoot = (scene) => {
    if (!scene) {
        throw new Error("Сцена ещё не готова");
    }

    scene.updateMatrixWorld(true);

    const root = new THREE.Group();
    root.name = "Module3DAssembly";

    const details = [];
    scene.traverse((obj) => {
        if (obj.userData?.module3dRole === "detail") {
            details.push(obj);
        }
    });

    if (!details.length) {
        throw new Error("На сцене нет деталей для экспорта");
    }

    details.forEach((obj, index) => {
        const clone = obj.clone(true);
        clone.name = obj.userData?.exportName
            || obj.name
            || `detail-${index + 1}`;
        // Зафиксировать мировую позу в корне экспорта.
        obj.updateMatrixWorld(true);
        clone.matrix.copy(obj.matrixWorld);
        clone.matrix.decompose(clone.position, clone.quaternion, clone.scale);
        clone.matrixAutoUpdate = true;
        pruneNonMeshHelpers(clone);
        root.add(clone);
    });

    return root;
};

/**
 * Экспорт размещённых деталей в .glb и скачивание файла.
 * @param {THREE.Scene} scene
 * @param {{fileName?: string}} [options]
 */
export const exportModuleSceneToGlb = async (scene, options = {}) => {
    const root = buildModuleExportRoot(scene);
    const exporter = new GLTFExporter();
    const result = await exporter.parseAsync(root, {
        binary: true,
        onlyVisible: true,
        truncateDrawRange: true,
    });

    const blob = result instanceof ArrayBuffer
        ? new Blob([result], {type: "model/gltf-binary"})
        : new Blob([JSON.stringify(result)], {type: "model/gltf+json"});

    const fileName = sanitizeFileName(options.fileName || "module-3d");
    downloadBlob(blob, fileName);
    return fileName;
};
